import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import {
  Award,
  Building2,
  Users,
  Compass,
  ArrowRight,
  Globe2,
  Calendar,
} from "lucide-react";

const About = () => {
  const { t, lPath, isRTL } = useLanguage();

  const timelineItems = [
    {
      year: "1965",
      title: "Business Legacy Begins",
      desc: "Foundations laid in the wool trade, built on direct commercial relationships, integrity, and hands-on grading expertise.",
    },
    {
      year: "Growth",
      title: "Growth in Wool Trading",
      desc: "Scaling bulk raw wool handling and strengthening ties with sheep breeders, mandis, and regional producers.",
    },
    {
      year: "Expansion",
      title: "Expansion of Supplier & Customer Network",
      desc: "Establishing continuous supply partnerships with spinning mills, weavers, and industrial yarn manufacturing facilities.",
    },
    {
      year: "Hub Presence",
      title: "Bikaner Market Presence",
      desc: "Cementing a prime presence in Bikaner, Rajasthan — the recognized carpet wool epicentre of Northern India.",
    },
    {
      year: "Domestic",
      title: "Domestic Wool Sourcing",
      desc: "Systematizing specification-based procurement across major Indian wool belts to serve specialized mill batches.",
    },
    {
      year: "Today",
      title: "Developing International Sourcing",
      desc: "Actively building strategic partnerships with wool exporters and merchants across Australia, New Zealand, China, and Syria.",
    },
  ];

  const timelineRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const el = timelineRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate how far through the timeline container the scroll has progressed
      const entryOffset = windowHeight * 0.75;
      const totalHeight = rect.height;
      const scrolled = entryOffset - rect.top;

      const progress = scrolled / (totalHeight - 80);
      setScrollProgress(Math.min(Math.max(progress, 0), 1));
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About Us",
        "item": "https://skwoolens.trivanahospitality.com/en/about"
      }
    ]
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About S.K. Woollens",
    "description": "Six decades of raw wool trading expertise dating back to 1965 in Bikaner, Rajasthan.",
    "publisher": {
      "@type": "WholesaleStore",
      "name": "S.K. Woollens",
      "alternateName": "Bulaki Chand Kesari Chand"
    }
  };

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="About S.K. Woollens | Raw Wool Sourcing Legacy Since 1965 | Bikaner"
        description="Discover the 60-year raw wool heritage of S.K. Woollens (Bulaki Chand Kesari Chand). Founded in 1965 in Bikaner, Rajasthan, supplying quality raw wool to spinning mills."
        keywords="about S.K. Woollens, Bikaner wool trading history, Bulaki Chand Kesari Chand, raw wool suppliers Rajasthan, wool merchant Bikaner, wool legacy 1965"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/about"
        schema={[breadcrumbSchema, aboutSchema]}
      />
      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/30 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="max-w-3xl space-y-4">
            <div className="wool-badge">
              <Award className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Established 1965 | Bikaner</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
              {t("about.title")}
            </h1>
            <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
              {t("about.p1")}
            </p>
            <p className="text-sm sm:text-base text-wool-muted leading-relaxed">
              {t("about.p2")}
            </p>

            <div className="p-4 bg-white border border-wool-border rounded-xl text-xs sm:text-sm text-wool-slate">
              <span className="font-semibold text-wool-bronze block mb-0.5">
                Heritage Background:
              </span>
              {t("about.legacyNote")}
            </div>
          </div>
        </div>
      </section>

      {/* 2. Brand Positioning & Core Focus Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <Compass className="w-6 h-6 text-wool-bronze" />
            </div>
            <h3 className="text-lg font-bold text-wool-charcoal">
              Bikaner Wool Market Presence
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Rooted in Bikaner, Rajasthan, our trading presence allows us direct access to India's premier raw wool mandi and grading ecosystem.
            </p>
          </div>

          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <Building2 className="w-6 h-6 text-wool-bronze" />
            </div>
            <h3 className="text-lg font-bold text-wool-charcoal">
              B2B Raw Wool Supply
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              We focus exclusively on commercial raw wool trading and bulk supplies to spinning mills and carpet yarn manufacturers.
            </p>
          </div>

          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <Globe2 className="w-6 h-6 text-wool-bronze" />
            </div>
            <h3 className="text-lg font-bold text-wool-charcoal">
              International Sourcing Direction
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Expanding strategic sourcing dialogues across Australia, New Zealand, China, Syria, and other key international wool centres.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Visual Timeline: 1965 → Today */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <div className="wool-badge">
              <Calendar className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Milestones</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
              {t("about.timelineTitle")}
            </h2>
            <p className="text-xs sm:text-sm text-wool-muted">
              Decades of consistent B2B presence, expanding procurement channels, and customer trust.
            </p>
          </div>

          <div ref={timelineRef} className="relative">
            {/* Background static line */}
            <div className="hidden lg:block absolute top-0 bottom-0 start-1/2 -ms-px w-0.5 bg-wool-border/50"></div>

            {/* Scroll-Progress Thread line */}
            <div
              className="hidden lg:block absolute top-0 start-1/2 -ms-px w-0.5 bg-wool-bronze origin-top transition-all duration-75"
              style={{ height: `${scrollProgress * 100}%` }}
            ></div>

            <div className="space-y-8 lg:space-y-12">
              {timelineItems.map((item, idx) => {
                const isEven = idx % 2 === 0;
                // Node lights up slightly before the card enters alignment
                const nodeActive = scrollProgress >= (idx / (timelineItems.length - 1)) * 0.95;
                return (
                  <div
                    key={idx}
                    className={`relative flex flex-col lg:flex-row items-start ${isEven ? "lg:flex-row-reverse" : ""
                      }`}
                  >
                    {/* Content Card */}
                    <div className="w-full lg:w-1/2 lg:px-8">
                      <div className={`p-6 bg-wool-bg border rounded-2xl shadow-sm space-y-2 transition-all duration-500 ${nodeActive
                          ? "border-wool-bronze/40 bg-white shadow-md scale-[1.01]"
                          : "border-wool-border bg-wool-bg/55 opacity-75"
                        }`}>
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-extrabold rounded-md transition-colors duration-350 ${nodeActive ? "bg-wool-bronze text-white" : "bg-wool-sand text-wool-bronze-dark"
                          }`}>
                          {item.year}
                        </div>
                        <h4 className="text-base font-bold text-wool-charcoal">
                          {item.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>

                    {/* Timeline Node */}
                    <div className={`hidden lg:flex absolute start-1/2 -ms-3 top-6 w-6 h-6 rounded-full items-center justify-center border-4 shadow transition-all duration-350 ${nodeActive
                        ? "bg-wool-bronze border-wool-sand scale-110 shadow-lg"
                        : "bg-white border-wool-border"
                      }`}>
                      <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${nodeActive ? "bg-white" : "bg-wool-border"
                        }`}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Leadership & Partners Preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-wool-charcoal text-white rounded-3xl p-8 sm:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-3 max-w-xl">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-wool-sand uppercase tracking-wider">
              <Users className="w-4 h-4 text-wool-sand" />
              <span>Partner Leadership</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold font-display">
              Dedicated Partners at S.K. Woollens
            </h3>
            <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed">
              Led by Sunil Banthia and Nikhil Banthia, managing domestic wool procurement, Bikaner operations, and expanding international sourcing dialogues.
            </p>
          </div>

          <Link
            to={lPath("/partners")}
            className="btn-primary bg-wool-sand text-wool-charcoal hover:bg-white text-xs shrink-0"
          >
            <span>View Partner Profiles</span>
            <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
